import React, { useState, useEffect } from 'react';
import './FileSummaryModal.css';

const FileSummaryModal = ({
  isOpen = false,
  onClose = () => {},
  fileName = 'File Summary',
  summary = '',
  fullContent = '',
  icon = null, // Optional custom icon
  loading = false,
  error = null,
  onSeeMore = null, // Optional callback for "See More" action
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayContent, setDisplayContent] = useState('');

  useEffect(() => {
    // Reset expansion state when modal opens/closes
    if (!isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  useEffect(() => {
    // Set content based on expansion state
    if (isExpanded && fullContent) {
      setDisplayContent(fullContent);
    } else {
      setDisplayContent(summary);
    }
  }, [isExpanded, summary, fullContent]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleSeeMore = () => {
    if (onSeeMore) {
      onSeeMore();
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="file-summary-backdrop" onClick={handleBackdropClick}>
      <div className="file-summary-modal">
        {/* Header */}
        <div className="file-summary-header">
          <div className="file-summary-title-wrapper">
            {icon || (
              <svg className="file-summary-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" opacity="0.3"/>
                <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" fill="none" stroke="currentColor" strokeWidth="2"/>
                <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            <h2 className="file-summary-title">{fileName}</h2>
          </div>
          <button 
            className="file-summary-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="file-summary-content">
          {loading ? (
            <div className="file-summary-loading">
              <div className="loading-spinner"></div>
              <p>Generating summary...</p>
            </div>
          ) : error ? (
            <div className="file-summary-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p className="error-message">{error}</p>
              <button className="retry-button" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className={`file-summary-text ${isExpanded ? 'expanded' : ''}`}>
                {displayContent || 'No summary available for this file.'}
              </div>
              
              {fullContent && fullContent !== summary && (
                <button 
                  className="file-summary-see-more"
                  onClick={handleSeeMore}
                >
                  {isExpanded ? 'See Less' : 'See More'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileSummaryModal;